import '../../tables/Table.module.css'

import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect, useContext} from 'react';
import { Workbook } from 'exceljs';
import Papa from 'papaparse';

import DataUploadTable from './DataUploadTable';
import FileDropZone from './FileDropZone';
import InfoPopup from '../../popup_windows/InfoPopup';
import PaginatedTable from '../../tables/PaginatedTable';
import InfoExperimentalProcedure from './InfoExperimentalProcedure';

// imports relevant for HTTP calls
import DisplayException from '../../error_messages/DisplayException';
import CircleLoader from 'react-spinners/CircleLoader';
import { SERVER_URL } from '../../config_server_url';
import UserSessionContext from '../../user_session/context_user_session';


function UploadManager(props){

    const { user } = useAuth0();

    // ------------------ Display HTTP/Server Exceptions ---------------------------------
    const [Exception, setException] = useState(false);
    const [ExceptionMessage, setExceptionMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [tableColNames, setTableColNames] = useState([]);
    const [uploadedData, setUploadedData] = useState([]);
    const [rawDataFiles, setRawDataFiles]= useState([]);
    const [rawDataFilenames, setRawDataFilenames] = useState([]);
    const [numSelectedRawData, setNumSelectedRawData] = useState(0);
    const [descriptionProcedure, setDesciptionProcedure] = useState('<p><em style="color: rgb(127, 127, 127);">Enter details about experimental method(s) and the uplaoded data ...</em></p>');
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');

    const usr_session_ctx = useContext(UserSessionContext);

    useEffect(() => {
        let factor_names = props.factors.map((factor) => {
            if(factor.units === ''){
                return `${factor.name}` 
            }
            else{
                return `${factor.name} [${factor.units}]` 
            }
        });
        let subscore_names = props.subscores.map((score) => {
            if(score.units === ''){
                return `${score.name}` 
            }
            else{
                return `${score.name} [${score.units}]` 
            }
        });
        let table_col_names = [...factor_names, ...subscore_names];
        setTableColNames(table_col_names);
    }, [props.factors, props.subscores]);   


    function onOkNotification(){
        setShowNotification(false);
        setNotificationMessage('');
    }


    async function handleUpload(){
        if (uploadedData.length <1){
            setShowNotification(true);
            setNotificationMessage(`There are no uploaded data entries yet.`);
            return;
        }
        // Convert uploaded data to CSV-Data 
        function transpose(matrix) {
            return matrix[0].map((_, columnIndex) => matrix.map(row => row[columnIndex]));
        }
        let uploadedDataTransposed = transpose(uploadedData)
        
        let factor_names = props.factors.map((factor) => factor.name );
        let factor_units = props.factors.map((factor) => factor.units );
        let subscore_names = props.subscores.map((score) => score.name );
        let subscore_units = props.subscores.map((score) => score.units );
        let csv_col_names = [...factor_names, ...subscore_names]
        let csv_col_units = [...factor_units, ...subscore_units]
        let csv_columns = csv_col_names.map ( (colName, id) => ({name:colName, units:csv_col_units[id], vals:uploadedDataTransposed[id]}));
        let csv_data = {columns:csv_columns}
        // console.log(csv_data);

        setIsLoading(true);
        try{
            let form_data = new FormData();
            if(rawDataFiles.length<1){
                let emptyBlob = new Blob()
                form_data.append('files', emptyBlob, 'empty_blob');
            }
            else{
                for (let i = 0; i < rawDataFiles.length; i++) {
                    const blob = new Blob([rawDataFiles[i].binary_data]);
                    form_data.append('files', blob, rawDataFiles[i].filename);
                }
            }
            let data = {
                project_uuid: usr_session_ctx.session_info.uuid_current_project,
                uuid_opt_campaign: props.uuid_opt_campaign,
                cycle_id: props.cycle_id,
                csv_data: csv_data,
                description_procedure: descriptionProcedure };
            form_data.append('data', JSON.stringify(data));

            let headers = {
                // 'Content-Type': 'multipart/form-data',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };

            const response = await fetch(`${SERVER_URL}/current_opt_campaign/upload_csv_results/`, {
                            method: 'POST',
                            headers: headers,
                            body: form_data
            });
            if(response.ok){
                const status = await response.json();
                if(status.msg !== ''){
                    setShowNotification(true);
                    setNotificationMessage(status.msg);
                    setIsLoading(false);
                }
                else{
                    setIsLoading(false);
                    props.handleExit('on_upload');
                }
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
            setIsLoading(false);
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }

    async function handleUploadRawData(uploadedFiles){
        let binaryData = [];
        setIsLoading(true);

        try {
            const readFilePromises = Array.from(uploadedFiles).map(file => {
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  resolve({ name: file.name, binaryStr: reader.result });
                };
                reader.onerror = () => {
                  reject(new Error('Error reading file'));
                };
                reader.readAsArrayBuffer(file);
              });
            });
        
            const results = await Promise.all(readFilePromises);
            binaryData = results.map(result => (
                { filename: result.name,
                  binary_data:result.binaryStr})
            );
            // Log the results
            // results.forEach(result => {
            //   console.log(result.binaryStr);
            //   console.log(result.name);
            // });
          } catch (err) {
            console.log('Error reading or parsing files:', err.message);
            binaryData = [];
          } finally {
            setIsLoading(false);
          }
        
        //   console.log(binaryData); // Log all binary data after all files are processed
          let raw_data_filenames = binaryData.map( (data) => ({selected:false, filename:(<><div style={{color:'black'}}>{data.filename}</div></>)}) );
          setRawDataFilenames(prevFilenames => [...prevFilenames, ...raw_data_filenames]);
          setRawDataFiles( prevRawData => [...prevRawData, ...binaryData]);
    }


    async function handleUploadData(uploadedFiles){
        // run basic validations
        if(uploadedFiles.length>1){
                setShowNotification(true);
                setNotificationMessage('Received multiple files. Expect data as single file.');
                return;
        }
        const file = uploadedFiles[0];
        const supportedFileTypes = ['text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
       ]

       if (!supportedFileTypes.includes(file.type)){
        setShowNotification(true);
        setNotificationMessage((<>Received unsuported file type <strong>{file.type}</strong>. Expect .csv or .xlsx file format.</>));
        return;
       }
       setIsLoading(true);
       let jsonData = [];
       if(file.type === 'text/csv') {
            await new Promise((resolve, reject) => {
                Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        jsonData=results.data;
                        resolve(results.data);
                    },
                    error: (error) => {
                        console.error('Error parsing CSV file:', error);
                        reject([])
                    },
                });
            });
        }
        else {
            await new Promise((resolve, reject) => {
                // Read the file as Json
                const reader = new FileReader();
                reader.onload = async (e) => {
                    try {
                        const arrayBuffer = e.target.result;
                        const workbook = new Workbook();
                        await workbook.xlsx.load(arrayBuffer);
    
                        const worksheet = workbook.worksheets[0]; // Assuming the first sheet
                        worksheet.eachRow({ includeEmpty: true }, (row) => {
                            const safeValues = row.values.map((cell) => {
                              if (cell && typeof cell === "object") {
                                if (cell.formula && cell.formula.includes('#REF!')) {
                                    console.warn(`Found #REF! in formula: ${cell.formula}`);
                                    return '#REF!'; // Handle #REF! error by returning a placeholder
                                  }
                                return cell.result ?? ""; // Use formula result if available
                              }
                              return cell ?? ""; // Use normal value or empty string
                            });
                            jsonData.push(safeValues);
                          });
                        resolve(jsonData);
                    } catch (err) {
                        console.log('Error reading or parsing the Excel file:', err.message);
                        reject([]);
                    }
                };

                reader.onerror = (err) => {
                    console.error('FileReader error:', err);
                    reject([]); // Reject on readAsArrayBuffer failure
                };
                    reader.readAsArrayBuffer(file); // Initiates the async read
            });
        }

        // step 1: convert json into rows
        let rows = jsonData.map((entry_line) => {
            let row = [];
            Object.entries(entry_line).map(([key, value]) => {
                row.push(value)}
            );
            if(row.length>0){
                return row;
            }
            else {return undefined}
        }).filter(element => element !== undefined);

        // column names in react state are not awailable here -> therefore we have to recreate them here
        let factor_names = props.factors.map((factor) => {
            if(factor.units === ''){
                return `${factor.name}` 
            }
            else{
                return `${factor.name} [${factor.units}]` 
            }
        });
        let subscore_names = props.subscores.map((score) => {
            if(score.units === ''){
                return `${score.name}` 
            }
            else{
                return `${score.name} [${score.units}]` 
            }
        });
        let table_col_names = [...factor_names, ...subscore_names];

        // set 2: search for table head with factor names and responses
        //        Expect factor_name[0] at cell A, aka 1st elemetn of a row; if not return wrong format error
        //         
        // strip rows that don't start with first factor name:

        let rows_stripped = []
        // let num_stripped  = 0;
        for (let i = 0; i < rows.length; i++) {
            if (rows[i][0] === table_col_names[0]) {
                rows_stripped = rows.splice(i); // Remove elements up to and including the found index
                // num_stripped = i;
                break;
            } else if (i === rows.length - 1) {
                rows_stripped = []; // Clear the array if no match is found
            }
        }
        // if first column name could not be found 
        if(rows_stripped.length < 1){
            setIsLoading(false);
            setShowNotification(true);
            setNotificationMessage(( 
                <>
               <div  style={{textAlign:'left'}}> 
                    <p>Invalid input format. Could not find column names:</p>
                    <ul>
                        {table_col_names.map((colName, index) => (
                        <li key={index}>{colName}</li>
                        ))}
                    </ul>
                    <p>To ensure proper formatting, download the template .xlsx.</p>
                </div>
                </>));
            return;
        }
        // test if all column names can be found
        for (let i = 0; i < table_col_names.length; i++){
            if (rows_stripped[0][i] !== table_col_names[i]){
                setIsLoading(false);
                setShowNotification(true);
                setNotificationMessage(( 
                    <>
                   <div  style={{textAlign:'left'}}> 
                        <p>Invalid input format. Expected column names:</p>
                        <ul>
                            {table_col_names.map((colName, index) => (
                            <li key={index}>{colName}</li>
                            ))}
                        </ul>
                        <p>Received column names:</p>
                        <ul style={{alignItems:'left'}}>
                            {rows_stripped[0].map((colName, index) => (
                            <li key={index}>{colName}</li>
                            ))}
                        </ul>
                        <p>To ensure proper formatting, download the template .xlsx.</p>
                        </div>
                    </>));
                return;
            }
        }


        // test if column entries containe NaN's - empty entries '' are valid
        for (let i = 1; i < rows_stripped.length; i++) {
            for (let j = 0; j < rows_stripped[i].length; j++) {
              if (isNaN(rows_stripped[i][j]) && rows_stripped[i][j] !== '') {
                setIsLoading(false);
                setShowNotification(true);
                setNotificationMessage(
                  <>
                    <p>
                      Detected unexpected NaN '{rows_stripped[i][j]}' {i} rows below column names:
                    </p>
                    <p>
                      {rows_stripped[i].map((entry, index) => (
                        <span key={index}>{entry}{index < rows_stripped[i].length - 1 ? ', ' : ''}</span>
                      ))}
                    </p>
                  </>
                );
                return;  // Stop further processing after the first unexpected NaN is found
              }
            }
          }

        let cleaned_rows = []
        // Fill rows with '' for missing entries to ensure that all row's are of equal length
        for (let i = 1; i < rows_stripped.length; i++) {
            let clean_row = [];
            for (let j = 0; j < table_col_names.length; j++) {
                if(rows_stripped[i][j] === ''){
                    clean_row.push('');
                }
                else if (!isNaN(rows_stripped[i][j])) {
                    clean_row.push(parseFloat(rows_stripped[i][j]));
                }
                else {
                    clean_row.push('');
                }
            }
            cleaned_rows.push(clean_row);
        }

        function transpose(matrix) {
            return matrix[0].map((_, columnIndex) => matrix.map(row => row[columnIndex]));
        }

        let cleaned_rows_transposed = transpose(cleaned_rows)
        // Test if type discrete match uploaded data.
        let num_factors = props.factors.length
        let notifications = [];
        props.subscores.forEach( (score, id) => {
            if(score.type === 'Discrete'){
                cleaned_rows_transposed[id+num_factors].forEach((val, idx)=> {
                    if(val !== ''){
                        if(!score.range_discrete.list_vals.includes(val)){
                            notifications.push( <>
                                <li style={{textAlign:'left'}}>Received invalid value val={val} in column '{table_col_names[id+num_factors]}'. 
                                Expect val to be in discrete list [{score.range_discrete.list_vals.join(', ')}]
                                </li>
                                </>);
                        }
                    }
                });
            } 
        });
        if (notifications.length > 0){
            setIsLoading(false);
            setShowNotification(true);
            setNotificationMessage(
                <>
                <div style={{textAlign:'left'}}>
                    <div>Invalid values for discrete Subscores:</div>
                    <ul>
                        {notifications}
                    </ul>
                </div>
                </>);
            return 
        }

        setUploadedData(cleaned_rows);
        setIsLoading(false);
    }

    function onSelectRawData(event){
        let parser = event.target.id.split('_')
        let file_id = parseInt(parser[1])
        let updatedRawDataFilenames = [...rawDataFilenames]
        updatedRawDataFilenames[file_id].selected = event.target.checked;
        setRawDataFilenames(updatedRawDataFilenames);

        let numSelected = updatedRawDataFilenames.filter(row => row['selected'] === true).length;
        setNumSelectedRawData(numSelected)
    }


    function handleRemoveRawData(){
        let updatedRawDataFilenames = []
        let updatedRawDataFiles = []
        rawDataFilenames.forEach((file, id) => {
            if(file['selected']===false) {
                updatedRawDataFilenames.push(file);
                updatedRawDataFiles.push(rawDataFiles[id]);

            }
        });
        setRawDataFilenames(updatedRawDataFilenames);
        setRawDataFiles(updatedRawDataFiles)
        setNumSelectedRawData(0)
    }


    function handleQillDescription(html) {
        setDesciptionProcedure(html);
    }


    let action_panel_raw_data=[]
    if (numSelectedRawData>0)
    {
        action_panel_raw_data=[{title:'remove', action:handleRemoveRawData}]
    }

    if(showNotification){
        return (<>
            <div className = {'builder_canvas'} >
                <InfoPopup ypos={'60px'} handleOk={onOkNotification} >
                    {notificationMessage}
                </InfoPopup>  
        </div>
        </>);
    }



    if(isLoading && !Exception){
        return (<div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'90%'}} >
                    <CircleLoader loading={isLoading} size={40} color='var(--aixelo-green)' />
            </div>);
    }

    if(Exception){
        return (<>
                <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'90%'}} >
                    <DisplayException>
                        {ExceptionMessage}
                    </DisplayException>  
                </div>
            </>);
    }
    return (<> 
        <div className = {'builder_canvas'} style={{height:props.height}} >
            <h3 style={{marginTop:'10px', marginLeft:'20px', padding:'0px', color:'var(--aixelo-red)'}}>Data Upload Manager</h3>
            <div style={{display:'flex'}}>
                <button className={'actionButtonSmall'} style={{marginLeft: '20px', right: '0px', marginBottom:'10px'}} onClick={handleUpload}>Upload</button>
                <button className={'actionButtonSmall'} style={{marginLeft: '0px', right: '0px', marginBottom:'10px'}} onClick={props.handleExit}>Exit</button>
            </div>
            <div style = {{width: `${props.width * 0.89 - 40}px`, marginLeft: '20px', background: 'white', marginTop: '10px', marginBottom: '20px', border:'0.5px solid var(--aixelo-gray-light)'}}>
                <InfoExperimentalProcedure 
                    width={props.width}
                    handleDescription={handleQillDescription}
                    description={descriptionProcedure}/>
            </div>


            <div style = {{width: `${props.width * 0.89 - 40}px`, marginLeft: '20px', background: 'white', marginTop: '10px', marginBottom: '0px', border:'0.5px solid var(--aixelo-gray-light)'}}>
                <h4 style = {{margin:'0px', marginTop:'10px', marginLeft:'10px', marginBottom:'10px', width:'fit-content'}} >
                    Uploaded Data 
                </h4>
                <div style = {{fontSize:'14px', alignItems:'top', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width: `${props.width * 0.89 - 60}px`, overflow:'auto'}}>
                    <DataUploadTable 
                        colNames={tableColNames}
                        tableRows={uploadedData}
                        // actionsOnSelect={action_panel}
                        // onSelect={props.onSelectCandidates}
                        itemsPerPage={15}
                        displayPrecision={null}
                        // handleDisplayPrecision={props.handleDisplayPrecision}
                        // handleCustomDesignInput={props.handleCustomDesignInput}
                        />
                </div>
                <h4 style = {{margin:'0px', marginTop:'10px', marginLeft:'10px', marginBottom:'10px', width:'fit-content'}} >
                    Uploaded Raw Data Files 
                </h4>
                <div style = {{fontSize:'14px', alignItems:'top', margin:'0px', marginLeft:'10px', marginBottom:'25px', height:'fit-content', width: `${props.width * 0.89 - 60}px`, overflow:'auto'}}>
                    <PaginatedTable
                        colNames={['', 'File Name']}
                        tableRows={rawDataFilenames}
                        itemsPerPage={15}
                        actionsOnSelect={action_panel_raw_data}
                        onSelect={onSelectRawData}
                        // handleDisplayPrecision={props.handleDisplayPrecision}
                        // handleCustomDesignInput={props.handleCustomDesignInput}
                        />
                </div>
                <div style={{width: `${props.width * 0.89 - 60}px`, marginLeft: '10px', display:'flex' }}>
                    <div style={{width: `${(props.width * 0.89 - 60)/2.-5}px`, marginRight:'5px'}}>
                        <FileDropZone 
                            handleLoadFiles={handleUploadData}
                            showTextOnActive={(<>Drop the file here ...</>)}
                            showText={(<>Upload results as a single file.<br/>Drag 'n' drop or click to select data file</>)}
                        />
                    </div>
                    <div style={{width: `${(props.width * 0.89 - 60)/2.-5}px`,  marginLeft:'5px'}}>
                        <FileDropZone 
                            handleLoadFiles={handleUploadRawData}
                            showTextOnActive={(<>Drop the files here ...</>)}
                            showText={(<>Upload associated raw data files. raw data files.<br/>Drag 'n' drop or click to select raw data files</>)}
                        />
                    </div>
                </div>
                <div style={{width: `${props.width * 0.89 - 60}px`, marginLeft: '10px', display:'flex'}}>
                    <button className={'actionButtonSmall'} style={{marginTop: '10px', right: '0px', width:'140px', marginBottom:'10px'}} onClick={props.returnTemplateXLS}>Get Template .xlsx</button>
                </div>
            </div>
        </div>
    </>);



}

export default UploadManager